/**
 * Facebook Messenger Platform - Message Reads Webhook Types
 * 
 * These types represent the webhook event structure for message_reads events.
 * Triggered when a user reads messages sent by a Page.
 * 
 * The message_reads webhook event indicates that all messages up to a certain 
 * watermark timestamp have been read by the recipient.
 * 
 * @see https://developers.facebook.com/docs/messenger-platform/reference/webhook-events/message-reads/
 */

import {
  BaseWebhookEvent,
  WebhookEventType,
  WebhookPayload,
} from './base-types';

// Note: MessageReadsSender and MessageReadsRecipient are now replaced by
// WebhookSender and WebhookRecipient from base-types.ts
// Keeping these as deprecated aliases for backward compatibility

import { WebhookSender as MessageReadsSender } from './base-types';
import { WebhookRecipient as MessageReadsRecipient } from './base-types';
export { MessageReadsSender, MessageReadsRecipient };

/**
 * Represents the read receipt information
 */
export interface MessageRead {
  /** 
   * Watermark timestamp indicating all messages up to this point have been read.
   * This is a Unix timestamp in milliseconds.
   * All messages with a timestamp less than or equal to this value have been read.
   */
  watermark: number;
}

/**
 * Main webhook event structure for message reads with discriminator
 * 
 * This event is triggered when a user reads messages sent by a Page.
 * The watermark indicates the timestamp up to which all messages have been read.
 * 
 * @example
 * ```json
 * {
 *   "type": "read",
 *   "sender": {
 *     "id": "1234567890123456"
 *   },
 *   "recipient": {
 *     "id": "9876543210987654"
 *   },
 *   "timestamp": 1458668856463,
 *   "read": {
 *     "watermark": 1458668856253
 *   }
 * }
 * ```
 */
export interface MessageReadsWebhookEvent extends BaseWebhookEvent {
  /** Discriminator for type narrowing */
  type: WebhookEventType.MESSAGE_READ;
  
  /** Read receipt information containing the watermark */
  read: MessageRead;
}

/**
 * Complete webhook payload structure that includes the message reads event
 * along with other webhook metadata
 */
export interface MessageReadsWebhookPayload extends WebhookPayload<MessageReadsWebhookEvent> {}

/**
 * Type guard to check if a webhook event is a message reads event
 * 
 * @param event - The webhook event to check
 * @returns True if the event contains a read property
 * 
 * @example
 * ```typescript
 * if (isMessageReadsEvent(event)) {
 *   // TypeScript now knows event has read property
 *   console.log(`Messages read up to timestamp: ${event.read.watermark}`);
 * }
 * ```
 */
export function isMessageReadsEvent(event: any): event is MessageReadsWebhookEvent {
  return event && typeof event === 'object' && 'read' in event;
}

/**
 * Utility type for extracting just the read data from a webhook event
 */
export type MessageReadData = MessageRead;

/**
 * Utility type for common message reads properties that might be used in processing
 */
export interface MessageReadsProcessingContext {
  /** The user who read the messages */
  senderId: string;
  
  /** The page that sent the messages */
  recipientId: string;
  
  /** Timestamp up to which all messages have been read */
  watermarkTimestamp: number;
  
  /** When the read event occurred */
  readTimestamp: number;
  
  /** 
   * Human-readable datetime for the watermark timestamp.
   * Useful for logging and debugging.
   */
  watermarkDate: Date;
  
  /** 
   * Human-readable datetime for the read event timestamp.
   * Useful for logging and debugging.
   */
  readDate: Date;
}

/**
 * Helper function to extract processing context from a message reads event
 * 
 * @param event - The message reads webhook event
 * @returns Simplified processing context with additional computed fields
 * 
 * @example
 * ```typescript
 * const context = extractMessageReadsContext(webhookEvent);
 * console.log(`User ${context.senderId} read messages up to ${context.watermarkDate.toISOString()}`);
 * ```
 */
export function extractMessageReadsContext(event: MessageReadsWebhookEvent): MessageReadsProcessingContext {
  return {
    senderId: event.sender.id,
    recipientId: event.recipient.id,
    watermarkTimestamp: event.read.watermark,
    readTimestamp: event.timestamp,
    watermarkDate: new Date(event.read.watermark),
    readDate: new Date(event.timestamp),
  };
}

/**
 * Helper function to check if a specific message timestamp was read
 * 
 * @param messageTimestamp - The timestamp of the message to check
 * @param watermark - The watermark timestamp from the read event
 * @returns True if the message with the given timestamp has been read
 * 
 * @example
 * ```typescript
 * const messageTime = 1458668855000;
 * const watermark = 1458668856253;
 * 
 * if (isMessageRead(messageTime, watermark)) {
 *   console.log('This message has been read');
 * }
 * ```
 */
export function isMessageRead(messageTimestamp: number, watermark: number): boolean {
  return messageTimestamp <= watermark;
}

/**
 * Helper function to determine which messages in a list have been read
 * 
 * @param messages - Array of messages with timestamp property
 * @param watermark - The watermark timestamp from the read event
 * @returns Array of messages that have been read
 * 
 * @example
 * ```typescript
 * const messages = [
 *   { id: '1', timestamp: 1458668855000, text: 'Hello' },
 *   { id: '2', timestamp: 1458668857000, text: 'World' }
 * ];
 * const watermark = 1458668856253;
 * 
 * const readMessages = getReadMessages(messages, watermark);
 * // Returns only the first message as it's before the watermark
 * ```
 */
export function getReadMessages<T extends { timestamp: number }>(
  messages: T[], 
  watermark: number
): T[] {
  return messages.filter(message => isMessageRead(message.timestamp, watermark));
}

/**
 * Helper function to get the count of read messages from a list
 * 
 * @param messages - Array of messages with timestamp property
 * @param watermark - The watermark timestamp from the read event
 * @returns Number of messages that have been read
 * 
 * @example
 * ```typescript
 * const messages = [
 *   { id: '1', timestamp: 1458668855000 },
 *   { id: '2', timestamp: 1458668857000 }
 * ];
 * const watermark = 1458668856253;
 * 
 * const readCount = getReadMessageCount(messages, watermark);
 * // Returns 1
 * ```
 */
export function getReadMessageCount<T extends { timestamp: number }>(
  messages: T[], 
  watermark: number
): number {
  return getReadMessages(messages, watermark).length;
}

/**
 * Constants related to message reads functionality
 */
export const MESSAGE_READS_CONSTANTS = {
  /** Webhook event type identifier */
  EVENT_TYPE: 'message_reads' as const,
  
  /** 
   * Property name in the webhook event that contains read data.
   * Used for type guards and event identification.
   */
  READ_PROPERTY: 'read' as const,
} as const;

/**
 * Type for the watermark timestamp
 * Represents a Unix timestamp in milliseconds indicating read status
 */
export type WatermarkTimestamp = number;

/**
 * Interface for objects that have a watermark (used in generic functions)
 */
export interface HasWatermark {
  /** Watermark timestamp */
  watermark: WatermarkTimestamp;
}

/**
 * Interface for objects that have a timestamp (used in message filtering)
 */
export interface HasTimestamp {
  /** Message timestamp */
  timestamp: number;
}